
/// <amd-module name="VSSUI/Components/PeoplePickerDropdown/PeoplePickerDropdown" />
import * as React from "react";
import { IPeoplePickerDropdownProps, IPeoplePickerDropdown } from "./PeoplePickerDropdown.Props";
import { IIdentity } from "../PeoplePicker/SharedPeoplePicker.Props";
import { Suggestions } from "../PeoplePicker/Suggestions";
import { VssPersona } from "VSSUI/VssPersona";
import { css, KeyCode } from "VSSUI/Util";
import { getSignInAddress, isUser } from "../PeoplePicker/PeoplePickerUtils";
import { PersonaCard } from "../PersonaCard";
import { TimerManagement } from "VSS/Core/TimerManagement";
import * as Resources from "VSSUI/VSS.Resources.VSSUI";
import { TextField } from "VSSUI/TextField";
import { Button } from "VSSUI/Button";
import { Observer } from "VSSUI/Observer";
import { VssIcon } from "VSSUI/VssIcon";
import { ObservableValue } from "VSS/Core/Observable";

//TODO: Remove this in a future breaking change.
import { Callout, DirectionalHint } from "OfficeFabric/Callout";
import { SuggestionsController } from "OfficeFabric/Pickers";

/** @deprecated Please use import { IdentityPickerDropdown } from "VSSUI/IdentityPicker" */
export class PeoplePickerDropdown extends React.Component<IPeoplePickerDropdownProps> implements IPeoplePickerDropdown {
    public static defaultProps = {
        calloutWidth: 296
    };

    private suggestionsVisible = new ObservableValue<boolean>(false);
    private cachedResults: { [id: string]: IIdentity[] };
    private currentPromise: PromiseLike<any>;
    private hasFocus = new ObservableValue<boolean>(false);
    private inputElement = React.createRef<TextField>();
    private loadingTimer: number | undefined;
    private openedIdentityCard = new ObservableValue<IIdentity>(undefined);
    private outerElement = React.createRef<HTMLDivElement>();
    private lastPickedIdentity: IIdentity;
    private selectedIdentity = new ObservableValue<IIdentity>(undefined);
    private suggestionsControl = React.createRef<Suggestions<IIdentity>>();
    private suggestionsLoading = new ObservableValue<boolean>(false);
    private suggestionStore: SuggestionsController<IIdentity>;
    private textValue = new ObservableValue<string>("");
    private timerManagement: TimerManagement;

    constructor(props: IPeoplePickerDropdownProps) {
        super(props);

        this.cachedResults = {};
        this.suggestionStore = new SuggestionsController<IIdentity>();
        this.timerManagement = new TimerManagement();
        this.lastPickedIdentity = props.defaultIdentity;
    }

    public render(): JSX.Element {
        return (
            <Observer
                suggestionsVisible={this.suggestionsVisible}
                hasFocus={this.hasFocus}
                openedIdentityCard={this.openedIdentityCard}
                selectedIdentity={this.selectedIdentity}
                textValue={this.textValue}
            >
                {(props: {
                    suggestionsVisible: boolean;
                    hasFocus: boolean;
                    openedIdentityCard: IIdentity;
                    selectedIdentity: IIdentity;
                    textValue: string;
                }) => {
                    const {
                        ariaLabel = Resources.PeoplePickerPlaceholderText,
                        autoFocus,
                        placeholder = Resources.PeoplePickerPlaceholderText
                    } = this.props;
                    const openClassName = (this.suggestionsVisible.value || props.hasFocus) && "bolt-peoplepickerdropdown-open";
                    return (
                        <div
                            className="bolt-peoplepickerdropdown flex-row flex-grow"
                            ref={this.outerElement}
                            onClick={this.onClick}
                            onKeyDown={this.onKeyDown}
                        >
                            <TextField
                                aria-autocomplete="none"
                                aria-label={ariaLabel}
                                aria-expanded={props.suggestionsVisible}
                                aria-haspopup="true"
                                autoFocus={autoFocus}
                                className={css(this.props.className, "bolt-peoplepickerdropdown-textField flex-row flex-center", openClassName)}
                                containerClassName="bolt-peoplepickerdropdown-container flex-column flex-grow"
                                onBlur={this.onBlur}
                                onChange={this.onSearchChange}
                                onFocus={this.onFocus}
                                prefixIconProps={{ render: this.renderPersonaCoin }}
                                placeholder={placeholder}
                                ref={this.inputElement}
                                role="comboBox"
                                suffixIconProps={{ className: "bolt-dropdown-button", iconName: "ChevronDown" }}
                                value={props.textValue}
                            />
                            {this.renderSuggestions()}
                            {props.openedIdentityCard && (
                                <PersonaCard
                                    getEntityFromUniqueAttribute={this.props.pickerProvider.getEntityFromUniqueAttribute}
                                    key={props.openedIdentityCard.entityId}
                                    identity={props.openedIdentityCard}
                                    target={this.outerElement.current}
                                    onDismissCallback={this.closePersonaCard}
                                    onRequestConnectionInformation={this.props.pickerProvider.onRequestConnectionInformation}
                                />
                            )}
                        </div>
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount(): void {
        this.onResolveSuggestions = this.timerManagement.debounce(this.onResolveSuggestions, 250);
        if (this.props.defaultIdentity) {
            this.suggestionStore.updateSuggestions([this.props.defaultIdentity], 0);
            this.selectedIdentity.value = this.props.defaultIdentity;
            this.textValue.value = this.props.defaultIdentity.displayName;
            this.suggestionsVisible.value = false;
        } else if (this.props.autoFocus) {
            this.updateSuggestionsList(this.props.pickerProvider.onEmptyInputFocus());
        }
    }

    public currentSuggestion = (): IIdentity => {
        return this.selectedIdentity.value;
    };

    public componentWillUnmount(): void {
        this.currentPromise = undefined;
    }

    public hidePicker = (): void => {
        this.suggestionsVisible.value = false;
        if (!this.hasFocus.value) {
            this.props.onBlur && this.props.onBlur();
        }
    };

    public showPicker = (): void => {
        this.suggestionsVisible.value = true;
    };

    public updateSuggestionsList(suggestions: IIdentity[] | PromiseLike<IIdentity[]>, initialSearchValue?: string): void {
        const suggestionsArray: IIdentity[] = suggestions as IIdentity[];
        const suggestionsPromiseLike: PromiseLike<IIdentity[]> = suggestions as PromiseLike<IIdentity[]>;
        // Check to see if the returned value is an array, if it is then just pass it into the next function.
        // If the returned value is not an array then check to see if it's a promise or PromiseLike. If it is then resolve it asynchronously.
        if (Array.isArray(suggestionsArray)) {
            this.updateSuggestions(suggestionsArray);
        } else if (suggestionsPromiseLike && suggestionsPromiseLike.then) {
            if (!this.loadingTimer) {
                this.loadingTimer = this.timerManagement.setTimeout(() => {
                    this.suggestionsLoading.value = true;
                }, 500);
            }

            // Ensure that the promise will only use the callback if it was the most recent one.
            const promise: PromiseLike<IIdentity[]> = (this.currentPromise = suggestionsPromiseLike);
            promise.then((newSuggestions: IIdentity[]) => {
                if (promise === this.currentPromise) {
                    this.suggestionsLoading.value = false;

                    this.updateSuggestions(newSuggestions, initialSearchValue);

                    if (this.textValue.value !== "" && this.suggestionStore.suggestions && this.suggestionStore.suggestions.length > 0) {
                        this.cachedResults[this.textValue.value] = newSuggestions;
                    }

                    if (this.loadingTimer) {
                        this.timerManagement.clearTimeout(this.loadingTimer);
                        this.loadingTimer = undefined;
                    }
                }
            });
        }
    }

    private renderPersonaCoin = (className: string): JSX.Element => {
        const iconClassName = "bolt-peoplepickerdropdown-icon flex-row flex-center justify-center";

        return (
            <Observer selectedIdentity={this.selectedIdentity}>
                {(props: { selectedIdentity: IIdentity }) => {
                    return props.selectedIdentity ? (
                        <VssPersona
                            className={iconClassName}
                            displayName={props.selectedIdentity.displayName}
                            imageUrl={props.selectedIdentity.image}
                            size="small"
                        />
                    ) : (
                        <VssIcon className={iconClassName} iconName="Contact" />
                    );
                }}
            </Observer>
        );
    };

    private closePersonaCard = (): void => {
        this.openedIdentityCard.value = undefined;
    };

    private completeSuggestion = (): void => {
        if (this.suggestionStore.suggestions.length > 0 && this.suggestionsVisible.value) {
            if (this.suggestionStore.currentIndex < 0) {
                this.suggestionStore.nextSuggestion();
            }
            this.setInputToSelectedPerson();
            this.suggestionStore.currentSuggestion && this.selectedPersonaChanged(this.suggestionStore.currentSuggestion.item);
            this.suggestionsVisible.value = false;
        } else {
            this.suggestionStore.deselectAllSuggestions();
            this.suggestionStore.currentSuggestion = undefined;
            this.selectedPersonaChanged();
        }
    };

    private onBlur = (): void => {
        this.hasFocus.value = false;
        this.timerManagement.setTimeout(() => {
            const areContactOrSuggestionsOpen =
                !this.openedIdentityCard.value && !this.suggestionsVisible.value && !this.suggestionsVisible.value;

            areContactOrSuggestionsOpen && this.props.onBlur && this.props.onBlur();
            areContactOrSuggestionsOpen && this.selectedPersonaChanged(this.selectedIdentity.value);
        }, 100);
    };

    private onClick = (): void => {
        if (this.textValue.value === "" && this.props.pickerProvider.onEmptyInputFocus) {
            this.updateSuggestionsList(this.props.pickerProvider.onEmptyInputFocus());
            this.suggestionsVisible.value = !this.suggestionsVisible.value;
        } else {
            this.suggestionsVisible.value = !this.suggestionsVisible.value;
        }
        this.inputElement.current.select();
    };

    private onFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
        const { onFocus } = this.props;
        if (this.textValue.value === "" && this.props.pickerProvider.onEmptyInputFocus && !this.suggestionsVisible.value) {
            this.updateSuggestionsList(this.props.pickerProvider.onEmptyInputFocus());
        }
        this.hasFocus.value = true;
        if (onFocus) {
            onFocus(e);
        }
    };

    private onKeyDown = (ev: React.KeyboardEvent<HTMLElement>): void => {
        const keyCode = ev.which;
        switch (keyCode) {
            case KeyCode.escape:
                if (this.openedIdentityCard.value) {
                    !this.suggestionStore.currentSuggestion && this.selectedPersonaChanged();
                }
                if (this.suggestionsVisible.value) {
                    this.suggestionsVisible.value = false;
                    this.openedIdentityCard.value = undefined;
                }

                break;

            case KeyCode.tab:
            case KeyCode.enter:
                if (this.suggestionsControl.current && this.suggestionsControl.current.hasSuggestedActionSelected()) {
                    this.suggestionsControl.current.executeSelectedAction();
                    this.completeSuggestion();
                } else if (!ev.shiftKey && this.suggestionStore.hasSelectedSuggestion() && this.suggestionsVisible.value) {
                    this.completeSuggestion();
                    ev.preventDefault();
                    ev.stopPropagation();
                } else if (this.suggestionsVisible.value) {
                    this.completeSuggestion();
                } else {
                    this.selectedPersonaChanged(this.selectedIdentity.value);
                }
                break;

            case KeyCode.rightArrow:
                if (this.suggestionStore.currentSuggestion) {
                    this.openedIdentityCard.value = this.suggestionStore.currentSuggestion.item;
                }
                break;

            case KeyCode.upArrow:
                if (this.suggestionsControl.current && this.suggestionsControl.current.tryHandleKeyDown(keyCode, this.suggestionStore.currentIndex)) {
                    ev.preventDefault();
                    ev.stopPropagation();
                } else {
                    if (this.suggestionsControl.current && this.suggestionsVisible.value) {
                        this.suggestionStore.previousSuggestion();
                        this.forceUpdate();
                        ev.preventDefault();
                        ev.stopPropagation();
                    }
                }
                break;

            case KeyCode.downArrow:
                if (this.suggestionsControl.current && this.suggestionsControl.current.tryHandleKeyDown(keyCode, this.suggestionStore.currentIndex)) {
                    ev.preventDefault();
                    ev.stopPropagation();
                } else {
                    if (this.suggestionsControl.current && this.suggestionsVisible.value) {
                        this.suggestionStore.nextSuggestion();
                        this.forceUpdate();
                        ev.preventDefault();
                        ev.stopPropagation();
                    } else {
                        this.suggestionsVisible.value = true;
                    }
                }

                break;
        }
    };

    private onResolveSuggestions = (updatedValue: string): void => {
        const suggestions: PromiseLike<IIdentity[]> | IIdentity[] = this.props.pickerProvider.onFilterIdentities(updatedValue, [
            this.suggestionStore.currentSuggestion && this.suggestionStore.currentSuggestion.item
        ]) as PromiseLike<IIdentity[]> | IIdentity[];

        if (suggestions !== null) {
            this.updateSuggestionsList(suggestions, updatedValue);
        }
    };

    private onSearchChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        this.suggestionsVisible.value = !!value;
        if (value.length == 0) {
            const { onClear } = this.props;
            onClear && onClear();
        }
        this.selectedIdentity.value = undefined;
        this.textValue.value = value;
        this.updateValue(value);
    };

    private onSuggestionClick = (ev: React.MouseEvent<HTMLElement>, item: IIdentity, index: number): void => {
        this.suggestionStore.setSelectedSuggestion(index);
        this.selectedPersonaChanged(item);
        this.textValue.value = item.displayName;
        this.hidePicker();
        ev.stopPropagation();
    };

    private renderNoIdentitiesFound = (): JSX.Element => {
        return <div className="bolt-peoplepickerdropdown-noresults flex-row flex-center">{Resources.PeoplePickerNoResultsText}</div>;
    };

    private renderSuggestionItem = (props: IIdentity, itemProps: any): JSX.Element => {
        const signInAddress = getSignInAddress(props);
        return (
            <div className="bolt-picker-suggesteditem flex-row flex-grow flex-center text-ellipsis">
                <VssPersona
                    cssClass="bolt-picker-persona text-ellipsis"
                    identityDetailsProvider={{
                        getIdentityImageUrl: (size: number): string => {
                            return props.image;
                        },
                        getDisplayName: (): string => {
                            return props.displayName;
                        }
                    }}
                    size="small"
                />

                <div className="bolt-picker-persona-name flex-column flex-grow">
                    <div className="fontSizeM text-ellipsis">{props.displayName}</div>
                    {signInAddress === "" ? undefined : <div className="fontSize secondary-text text-ellipsis">{signInAddress}</div>}
                </div>
                {isUser(props) && (
                    <Button
                        className="bolt-contact-card"
                        iconProps={{ iconName: "ContactCard", className: "bolt-contactcard-button flex-shrink" }}
                        onClick={(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                            this.openedIdentityCard.value = props;
                            e.stopPropagation();
                        }}
                        subtle={true}
                    />
                )}
            </div>
        );
    };

    private renderSuggestions(): JSX.Element | null {
        return this.suggestionsVisible.value || this.openedIdentityCard.value ? (
            <Callout
                calloutWidth={this.props.calloutWidth}
                className="bolt-peoplepickerdropdown-callout"
                directionalHint={DirectionalHint.bottomLeftEdge}
                isBeakVisible={false}
                onDismiss={this.hidePicker}
                target={this.outerElement.current}
            >
                <Suggestions
                    className="bolt-peoplepickerdropdown-list"
                    isLoading={this.suggestionsLoading.value}
                    isResultsFooterVisible={this.props.isResultsFooterVisible}
                    loadingText={Resources.PeoplePickerLoadingText}
                    onRenderNoResultFound={this.renderNoIdentitiesFound}
                    onRenderSuggestion={this.renderSuggestionItem}
                    onSuggestionClick={this.onSuggestionClick}
                    ref={this.suggestionsControl}
                    resultsMaximumNumber={this.props.resultsMaximumNumber}
                    suggestionsContainerAriaLabel={this.props.suggestionsContainerAriaLabel}
                    suggestionsItemClassName={css("bolt-peoplepickerdropdown-item", this.props.suggestionsItemClassName)}
                    suggestions={this.suggestionStore.getSuggestions()}
                />
            </Callout>
        ) : null;
    }

    private selectedPersonaChanged = (persona?: IIdentity): void => {
        this.selectedIdentity.value = persona;
        if (this.props.onChange && this.lastPickedIdentity !== persona) {
            !!persona && !!this.props.pickerProvider.addIdentitiesToMRU && this.props.pickerProvider.addIdentitiesToMRU([persona]);
            this.props.onChange(this.selectedIdentity.value);
        }
        this.lastPickedIdentity = persona;
    };

    private setInputToSelectedPerson = () => {
        const itemValue: string = this.suggestionStore.currentSuggestion
            ? this.suggestionStore.currentSuggestion.item.displayName
            : this.textValue.value;
        this.textValue.value = itemValue;
    };

    private updateSuggestions(suggestions: IIdentity[], initialSearchValue?: string): void {
        // Only update the suggestions if the initial search value is the same as the current input
        if (initialSearchValue === undefined || (this.inputElement.current && this.textValue.value === initialSearchValue)) {
            this.suggestionStore.updateSuggestions(suggestions, 0);
            if (initialSearchValue !== undefined) {
                this.selectedIdentity.value = undefined;
            }
            this.forceUpdate();
        }
    }

    private updateValue(updatedValue: string): void {
        if (this.cachedResults[updatedValue]) {
            this.updateSuggestionsList(this.cachedResults[updatedValue], updatedValue);
        } else {
            this.onResolveSuggestions(updatedValue);
        }
    }
}
